<template>
  <ModalConfirmCancellation
    v-show="modalConfirmCancellation"
    @closeConfirmCancellation="closeConfirmCancellationModal"
    @openReasonCancellationModal="manageReasonCancellationModal(true)"
    :message="message"
  />

  <ModalReasonCancellation
    v-show="modalReasonCancellation"
    @closeReasonCancellation="closeReasonCancellationModal"
    @nullify="nullifyPaymentTransaction"
  />

  <ModalReceipt
    v-show="modalReceipt"
    @closeReceipt="closeReceiptModal"
    :receiptUrl="selectedReceiptUrl"
  />

  <ModalImages
    v-show="modalImages"
    @closeImages="closeImagesModal"
    :listImages="selectedImages"
  />

  <div class="wrapper-payments">
    <div class="above-table">
      <p>Pagos</p>
      <DatePicker :label="'Fecha'" @change="handleDateChange" />
      <Searcher
        placeholder="Busca por no. provisional..."
        :isDisabled="payments.length === 0"
        v-model:search="query"
      />

      <div v-if="isDateSelected">
        <a :href="route" class="wrapper-button">
          <StandardButton
            :buttonLabel="'Exportar archivo'"
            :isDisabled="payments.length === 0"
          />
        </a>
      </div>
    </div>
    <div class="empty" v-if="payments.length === 0">
      <img
        src="@/assets/icons/NoResults.svg"
        alt="Sin resultados"
        class="icon"
      />
      <p class="text-empty">Selecciona una fecha para ver los pagos</p>
    </div>
    <table v-else>
      <thead>
        <tr>
          <th>No. provisional</th>
          <th>No. factura</th>
          <th>Vendedor</th>
          <th>Cliente</th>
          <th>Pago</th>
          <th>Fecha pago</th>
          <th>Valor original</th>
          <th>Valor pago</th>
          <th>Saldo</th>
          <th>Recibo</th>
          <th>Imágenes</th>
          <th>Estado</th>
          <th>Anular</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(payment, index) in filteredPayments" :key="index">
          <td>{{ payment.orderNumber }}</td>
          <td>{{ payment.invoceNumber }}</td>
          <td>{{ payment.sellerName }}</td>
          <td>{{ payment.clientName }}</td>
          <td>{{ payment.methodOfPayment }}</td>
          <td>{{ payment.paymentDate }}</td>
          <td>{{ formatPrice(payment.baseValue) }}</td>
          <td>{{ formatPrice(payment.value) }}</td>
          <td>{{ formatPrice(payment.balance) }}</td>
          <td>
            <TableButton type="Receipt" @click="openReceiptModal(payment)" />
          </td>
          <td>
            <TableButton type="Images" @click="openImagesModal(payment)" />
          </td>
          <td>{{ payment.state }}</td>
          <td>
            <div v-if="payment.state !== 'ANULADO'">
              <TableButton
                type="Cancel"
                @click="manageConfirmCancellationModal(payment)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import ModalConfirmCancellation from "../sharedComponents/ModalConfirmCancellation.vue";
import ModalReasonCancellation from "../sharedComponents/ModalReasonCancellation.vue";
import ModalReceipt from "../sharedComponents/ModalReceipt.vue";
import ModalImages from "../sharedComponents/ModalImages.vue";
import { paymentsUse } from "./use/paymentsUse";
import TableButton from "@/components/commons/controls/TableButton.vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import Searcher from "@/components/commons/controls/Searcher.vue";
import DatePicker from "@/components/commons/controls/DatePicker.vue";
import { spinnerProcessKey, successProcessKey } from "@/main";

export default defineComponent({
  name: "PaymentsView",
  components: {
    ModalConfirmCancellation,
    ModalReasonCancellation,
    ModalReceipt,
    ModalImages,
    TableButton,
    StandardButton,
    Searcher,
    DatePicker,
  },

  setup() {
    const {
      manageConfirmCancellationModal,
      modalConfirmCancellation,
      closeConfirmCancellationModal,
      manageReasonCancellationModal,
      modalReasonCancellation,
      closeReasonCancellationModal,
      payments,
      selectedDate,
      handleDateChange,
      query,
      filteredPayments,
      route,
      openReceiptModal,
      modalReceipt,
      closeReceiptModal,
      selectedReceiptUrl,
      openImagesModal,
      selectedImages,
      modalImages,
      closeImagesModal,
      isDateSelected,
      nullifyPaymentOperation,
      message,
    } = paymentsUse();

    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    function formatPrice(price: number | undefined | null): string {
      if (price === undefined || price === null) return "$0";

      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    }

    async function nullifyPaymentTransaction(comments: string) {
      try {
        await onSpinnerState();
        await nullifyPaymentOperation(comments);
        await offSpinnerState();
        showSuccessModal("Pago anulado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    return {
      manageConfirmCancellationModal,
      modalConfirmCancellation,
      closeConfirmCancellationModal,
      manageReasonCancellationModal,
      modalReasonCancellation,
      closeReasonCancellationModal,
      payments,
      selectedDate,
      handleDateChange,
      query,
      filteredPayments,
      route,
      formatPrice,
      openReceiptModal,
      modalReceipt,
      closeReceiptModal,
      selectedReceiptUrl,
      openImagesModal,
      modalImages,
      closeImagesModal,
      selectedImages,
      isDateSelected,
      nullifyPaymentTransaction,
      message,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/payments/styles/paymentsStyle";
</style>
